// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  AssemblyItemId,
  PartId,
  AssemblyTaskId,
  AssemblyPartItem,
  Part,
  TaskTypeId,
  FileId,
} from '@evolvemep/api-ts';
import type { Node, NodeTypes } from '@xyflow/react';

import type { WithOptional, StrongOmit } from 'helpers/helper-types';
import { PartNode, TaskNode } from 'modules/Shop/CatalogSetup/PartEditor/nodes/AssemblyNode';
import type { CoreNodeId } from 'modules/Shop/CatalogSetup/PartEditor/types';

export type NodeReferenceId = typeof CoreNodeId | PartId | AssemblyItemId | AssemblyTaskId;

export type PartNodeType = Node<{
  part: StrongOmit<AssemblyPartItem, 'assemblyItemId' | 'unitQuantity'>;
  quantity: number;
}>;

export type WriteInNodeType = Node<{
  part: WithOptional<
    StrongOmit<AssemblyPartItem, 'partId' | 'partTypeId' | 'assemblyItemId' | 'unitQuantity'>,
    'manufacturerId' | 'manufacturerName'
  >;
  quantity: number;
}>;

export type TaskNodeType = Node<{
  taskType: {
    taskTypeId: TaskTypeId;
    taskTypeName: string;
    taskTypeDescription?: string | null;
    imageFileId?: FileId;
  };
}>;

export type AssemblyNodeType = Node<{
  part: WithOptional<AssemblyPartItem, 'partId'>;
}>;

export type CoreNodeType = Node<{
  part: Pick<Part, 'name' | 'description' | 'unitOfMeasureName' | 'unitOfMeasureCode' | 'image'>;
}>;

export type NodeType = PartNodeType | WriteInNodeType | AssemblyNodeType | CoreNodeType | TaskNodeType;

export const assemblyNodeTypes = {
  'assembly': PartNode,
  'part': PartNode,
  'write-in': PartNode,
  'core': PartNode,
  'task': TaskNode,
} as const satisfies NodeTypes;
